import { useEvent } from "client-util/useEvent";
import { useShallowMemo } from "client-util/useShallowMemo";
import { produce } from "immer";
import { useEffect } from "react";
import {
  MovableType,
  Box,
  BoxPiece,
  makeBoxPieceId,
  Movable,
  Piece,
  RoundScopeUpdate,
  RoundStepType,
  parseBoxPieceId,
} from "sockapi/scope-specs/round-spec";
import { SockAPIScopeState } from "sockapi/scope-state";
import { ScopeType } from "sockapi/scope-types";
import { useRound } from "stores/NavStore";
import { useScopeStateResolvable } from "stores/ScopeStateStore";
import { useSockClient, useTeamId } from "stores/ServerInteractionStore";
import { StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import { createWithEqualityFn as create } from "zustand/traditional";

let lastPendingId = 0;

interface RoundState {
  /** Record from round ID to overrides. */
  rounds: Record<
    string,
    {
      /** Overrides that we've sent to server but haven't been acked yet. */
      pending: (RoundScopeUpdate & { id: number })[];
      /** Override we're currently working on. */
      draft: RoundScopeUpdate;
    }
  >;
  /** Update state. Patches, not replaces. */
  update: (roundId: string, args: RoundScopeUpdate) => void;
  /**
   * Commit the current draft, moving it to pending. Intended to be called
   * right before sending the draft to the server.
   */
  commit: (roundId: string) => {
    /** Draft to send to server. */
    draft: RoundScopeUpdate | null;
    /** Call after server response, whether it's confirmed or discarded. */
    remove: () => void;
  };
}

const makeEmptyRoundState = () =>
  ({
    pending: [],
    draft: { movables: {} },
  }) as RoundState["rounds"][string];

const roundStateInitializer: StateCreator<RoundState> = (set, get) => ({
  rounds: {},
  update: (roundId, args) =>
    set(
      produce<RoundState>((state) => {
        state.rounds[roundId] ??= makeEmptyRoundState();
        for (const [movableId, movable] of Object.entries(args.movables)) {
          state.rounds[roundId].draft.movables[movableId] = Object.assign(
            state.rounds[roundId].draft.movables[movableId] ?? {},
            movable
          );
        }
      })
    ),
  commit: (roundId) => {
    const draft = get().rounds[roundId].draft;

    if (Object.keys(draft.movables).length === 0) {
      // nothing to commit
      return {
        draft: null,
        remove: () => {},
      };
    }

    const id = ++lastPendingId;

    set(
      produce<RoundState>((state) => {
        state.rounds[roundId] ??= makeEmptyRoundState();
        state.rounds[roundId].pending.push({ id, ...draft });
        state.rounds[roundId].draft = { movables: {} };
      })
    );

    return {
      draft,
      remove: () =>
        set(
          produce<RoundState>((state) => {
            const index = state.rounds[roundId].pending.findIndex(
              (p) => p.id === id
            );
            state.rounds[roundId].pending.splice(index, 1);
          })
        ),
    };
  },
});

const useRoundStore = create<RoundState>()(devtools(roundStateInitializer));

type DerivedRoundState = {
  /** Record from movable ID to movable. */
  movables: Record<string, Movable>;
  /** List of box IDs. */
  boxIds: string[];
  /** List of piece IDs. */
  pieceIds: string[];
  /** Record from piece ID to box IDs it's inside in. */
  inside: Record<string, string[]>;
  /** Record from box ID to piece IDs that are inside it. */
  contained: Record<string, string[]>;
  /** List of piece IDs that aren't inside any box. */
  uncontained: string[];
};

interface DerivedRoundStore {
  rounds: Record<string, DerivedRoundState>;
  /** Update the derived round state. */
  update: (
    roundId: string,
    scope: SockAPIScopeState<ScopeType.ROUND> | null,
    store: ReturnType<typeof useRoundStore.getState>["rounds"][string] | null
  ) => void;
}

const derivedRoundStateInitializer: StateCreator<DerivedRoundStore> = (
  set
) => ({
  rounds: {},
  update: (roundId, scope, store) => {
    const { movables: scopeMovables = {} } = scope ?? {};
    const { pending, draft } = store ?? makeEmptyRoundState();

    const movableIds = Array.from(
      new Set([
        ...Object.keys(scopeMovables),
        ...pending.map((d) => Object.keys(d.movables)).flat(),
        ...Object.keys(draft.movables),
      ])
    );
    const boxIds = [] as string[];
    const pieceIds = [] as string[];
    const inside = {} as Record<string, string[]>;
    const contained = {} as Record<string, string[]>;
    const uncontained = [] as string[];

    const movables = structuredClone(scopeMovables);
    for (const movableId of movableIds) {
      if (!movables[movableId]) {
        movables[movableId] = {} as Movable;
      }
      for (const pendingState of pending) {
        const pendingMovable = pendingState.movables[movableId];
        if (pendingMovable) {
          Object.assign(movables[movableId], pendingMovable);
        }
      }
      const draftMovable = draft.movables[movableId];
      if (draftMovable) {
        Object.assign(movables[movableId], draftMovable);
      }

      const movable = movables[movableId];
      switch (movable.type) {
        case MovableType.BOX: {
          boxIds.push(movableId);
          contained[movableId] ??= [];
          break;
        }
        case MovableType.PIECE: {
          pieceIds.push(movableId);
          inside[movableId] ??= [];
          break;
        }
        case MovableType.BOX_PIECE: {
          if (movable.active) {
            const { boxId, pieceId } = parseBoxPieceId(movableId);
            (inside[pieceId] ??= []).push(boxId);
            (contained[boxId] ??= []).push(pieceId);
          }
          break;
        }
      }
    }

    for (const pieceId of pieceIds) {
      if (inside[pieceId].length === 0) {
        uncontained.push(pieceId);
      }
    }

    set(
      produce<DerivedRoundStore>((state) => {
        state.rounds[roundId] = {
          movables,
          boxIds,
          pieceIds,
          inside,
          contained,
          uncontained,
        };
      })
    );
  },
});

const useDerivedRoundStore = create<DerivedRoundStore>()(
  devtools(derivedRoundStateInitializer)
);

export const useRoundScopeListener = () => {
  const { roundId } = useRound() ?? {};
  const teamId = useTeamId();
  const scope = useScopeStateResolvable(
    ScopeType.ROUND,
    teamId && roundId ? { teamId, roundId } : null
  );
  const store = useRoundStore(
    useShallow((state) => (roundId ? state.rounds[roundId] : null))
  );
  const { update } = useDerivedRoundStore.getState();

  useEffect(() => {
    if (!roundId) return;
    update(roundId, scope, store);
  }, [roundId, scope, store, update]);
};

const getRoundStateNonReactive = ({ roundId }: { roundId: string }) => {
  const draft = useRoundStore.getState().rounds[roundId]?.draft;
  const derived = useDerivedRoundStore.getState().rounds[roundId];
  return { draft, derived };
};

export const useGetRoundStateNonReactive = () => {
  const { roundId } = useRound() ?? {};
  if (roundId === undefined) return null;
  return () => getRoundStateNonReactive({ roundId });
};

function useRoundState<T = DerivedRoundState>(
  selector?: (state: DerivedRoundState) => T
): T {
  const { roundId } = useRound() ?? {};
  return useDerivedRoundStore(
    useShallow((state) => {
      const slice = (roundId && state.rounds[roundId]) || {
        movables: {},
        boxIds: [],
        pieceIds: [],
        inside: {},
        contained: {},
        uncontained: [],
      };
      return selector ? selector(slice) : slice;
    })
  ) as T;
}

export const useMovable = <T extends Movable = Movable>(
  movableId: string
): T | null => {
  return useRoundState((state) => {
    return (state.movables[movableId] ?? null) as T | null;
  });
};

export const useBox = (
  boxId: string | null
): {
  box: Box | null;
  pieceIds: string[];
} => {
  const boxView = useRoundState((state) => {
    if (boxId === null) return null;
    const box = state.movables[boxId] ?? null;
    if (box?.type !== MovableType.BOX) {
      console.warn(`tried to useBox on ${boxId}, which is not a box`);
    }
    return box as Box | null;
  });

  const pieceIds = useRoundState((state) =>
    boxId ? state.contained[boxId] : state.uncontained
  );

  return { box: boxView, pieceIds };
};

export function useBoxPieces(
  boxId: string,
  pieceIds: string[]
): (BoxPiece | null)[] {
  const ids = pieceIds.map((pieceId) => makeBoxPieceId(boxId, pieceId));

  return useRoundState((state) => {
    return ids.map((id) => {
      const boxPiece = state.movables[id];
      if (boxPiece === undefined) return null;
      if (boxPiece.type !== MovableType.BOX_PIECE)
        throw new Error(
          `tried to useBoxPieces on ${id}, which is not a boxPiece`
        );
      return boxPiece;
    });
  });
}

export function usePiece(
  boxId: string | null,
  pieceId: string
): { piece: Piece | BoxPiece | null; box: Box | null; boxIds: string[] } {
  const id = makeBoxPieceId(boxId, pieceId);

  const pieceView = useRoundState((state) => {
    const piece = state.movables[id] ?? null;
    if (
      boxId
        ? piece?.type !== MovableType.BOX_PIECE
        : piece?.type !== MovableType.PIECE
    ) {
      console.warn(
        `tried to usePiece on ${id}, which is not a ${boxId ? "boxPiece" : "piece"}`
      );
    }
    return piece as Piece | BoxPiece | null;
  });

  const boxView = useRoundState((state) => {
    if (boxId === null) return null;
    const box = state.movables[boxId] ?? null;
    if (box?.type !== MovableType.BOX) {
      console.warn(
        `tried to usePiece on (${boxId}, ${pieceId}), which is not a box`
      );
    }
    return box as Box | null;
  });

  const boxIds = useRoundState((state) => {
    return state.inside[pieceId] ?? [];
  });

  if (boxIds && boxId && pieceView?.id && !boxIds.includes(boxId)) {
    console.warn(
      `tried to usePiece on (${boxId}, ${pieceView?.id}), but piece is not in box`
    );
  }

  return { piece: pieceView, box: boxView, boxIds };
}

export const useBoxIds = () => {
  return useRoundState((state) => state.boxIds);
};

export const usePieceIds = () => {
  return useRoundState((state) => state.pieceIds);
};

export const useMovables = () => {
  const movables = useRoundState((state) => state.movables);
  return useShallowMemo(() => Object.values(movables));
};

const makeScope = (teamId: string, roundId: string) =>
  ({
    type: ScopeType.ROUND,
    teamId,
    roundId,
  }) as const;

export const useSelectMovable = () => {
  const { roundId } = useRound() ?? {};
  const update = useRoundStore((state) => state.update);

  return useEvent((movableId: string) => {
    if (!roundId) return;
    const { draft, derived } = getRoundStateNonReactive({ roundId });
    const draftMovable = draft?.movables[movableId] ?? null;
    const movable = derived?.movables[movableId] ?? null;
    if (!movable) return;
    const version = draftMovable?.version ?? movable.version + 1;

    update(roundId, { movables: { [movableId]: { version } } });
  });
};

export const useUpdateMovableNoCommit = () => {
  const { roundId } = useRound() ?? {};
  const update = useRoundStore((state) => state.update);

  return useEvent((movableId: string, payload: Partial<Movable>) => {
    if (!roundId) return;
    const { draft, derived } = getRoundStateNonReactive({ roundId });
    const draftMovable = draft?.movables[movableId] ?? null;
    const movable = derived?.movables[movableId] ?? null;
    if (!payload.version && !movable) {
      console.warn(
        `tried to create new movable ${movableId} - ${payload} without version`
      );
      return;
    }
    const version =
      payload.version ?? draftMovable?.version ?? movable!.version + 1;

    update(roundId, { movables: { [movableId]: { version, ...payload } } });
  });
};

export const useCommit = () => {
  const teamId = useTeamId();
  const { roundId } = useRound() ?? {};
  const commit = useRoundStore((state) => state.commit);
  const sockClient = useSockClient();

  return useEvent(async () => {
    if (!teamId || !roundId) return;

    const { draft, remove } = commit(roundId);
    if (!draft) return;

    // send the update and wait for ack
    const { success = false } =
      (await sockClient
        .sendScopeStepAndWaitAsync(makeScope(teamId, roundId), {
          type: RoundStepType.UPDATE,
          movables: draft.movables,
        })
        .catch(() => ({ success: false }))) ?? {};

    if (!success) {
      console.error(`failed to commit draft ${JSON.stringify(draft)}`);
      // TODO: toast, probably
    }

    remove();
  });
};

export const useUpdateMovable = () => {
  const update = useUpdateMovableNoCommit();
  const commit = useCommit();

  return useEvent(
    async (movableId: string, payload: Partial<Omit<Movable, "version">>) => {
      update(movableId, payload);
      await commit();
    }
  );
};

const makeDefaultBoxPiece = (
  id: string,
  props?: Partial<BoxPiece> & Required<Pick<BoxPiece, "cols" | "rows">>
): BoxPiece => ({
  type: MovableType.BOX_PIECE,
  id,
  active: true,
  version: 1,
  order: 0,
  col: 0,
  row: 0,
  cols: 0,
  rows: 0,
  rot: 0,
  ...props,
});

type UpdatePieceInsideParams = {
  pieceId: string;
  boxId: string;
  add: boolean;
  props?: Partial<BoxPiece>;
};

export const useUpdatePieceInsideNoCommit = () => {
  const getRoundStateNonReactive = useGetRoundStateNonReactive();
  const update = useUpdateMovableNoCommit();

  return useEvent(({ pieceId, boxId, add, props }: UpdatePieceInsideParams) => {
    if (getRoundStateNonReactive === null)
      throw new Error(
        "tried to update piece before round state is initialized"
      );
    const { derived } = getRoundStateNonReactive();
    const boxPieceId = makeBoxPieceId(boxId, pieceId);
    const movable = derived?.movables[boxPieceId] ?? null;
    if (!movable) {
      if (add) {
        const piece = derived.movables[pieceId]!;
        update(
          boxPieceId,
          makeDefaultBoxPiece(boxPieceId, {
            ...props,
            cols: piece.cols,
            rows: piece.rows,
          })
        );
      } else {
        return;
      }
    } else {
      update(boxPieceId, { active: add, ...props });
    }
  });
};

export const useUpdatePieceInside = () => {
  const updateInside = useUpdatePieceInsideNoCommit();
  const commit = useCommit();

  return useEvent(async (params: UpdatePieceInsideParams) => {
    updateInside(params);
    await commit();
  });
};

export const useTransferPiece = () => {
  const updateInsideNoCommit = useUpdatePieceInsideNoCommit();
  const commit = useCommit();

  return useEvent(
    async ({
      pieceId,
      fromBoxId,
      toBoxId,
      props,
    }: {
      pieceId: string;
      fromBoxId: string;
      toBoxId: string;
      props?: Partial<BoxPiece>;
    }) => {
      if (fromBoxId !== toBoxId) {
        updateInsideNoCommit({
          pieceId,
          boxId: fromBoxId,
          add: false,
        });
      }
      updateInsideNoCommit({
        pieceId,
        boxId: toBoxId,
        add: true,
        props,
      });
      await commit();
    }
  );
};
